import { paths } from '@common/types';
import { axiosInstance } from '@config/axios/axios.config';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

// ---------- CREATE OPERATOR ----------

const createOperatorRequest = async (
  props: paths['/operator']['post']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.post<
    paths['/operator']['post']['requestBody']['content']['application/json'],
    AxiosResponse<
      paths['/operator']['post']['responses']['201']['content']['application/json']
    >
  >(`/operator`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useCreateOperatorMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['operator'],
    mutationFn: (
      props: paths['/operator']['post']['requestBody']['content']['application/json'],
    ) => createOperatorRequest(props),
    onSuccess: () => client.invalidateQueries({ queryKey: ['operators'] }),
  });

  return mutation;
};

// ---------- EDIT OPERATOR ----------

const editOperatorRequest = async (
  props: paths['/operator/{record_id}']['patch']['requestBody']['content']['application/json'] & {
    operator_id: string;
  },
) => {
  const response = axiosInstance.patch<
    paths['/operator/{record_id}']['patch']['requestBody']['content']['application/json']
  >(`/operator/${props.operator_id}`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useEditOperatorMutation = (operator_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['editOperator'],
    mutationFn: (
      props: paths['/operator/{record_id}']['patch']['requestBody']['content']['application/json'] & {
        operator_id: string;
      },
    ) => editOperatorRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['operators'] }),
        client.invalidateQueries({
          queryKey: ['operator', operator_id],
        }),
      ]),
  });

  return mutation;
};

// ---------- UPLOAD OPERATOR LOGO ----------

const uploadOperatorLogoRequest = async (props: {
  id: string;
  file: FormData;
}) => {
  const response = axiosInstance.put<{
    id: string;
    file: FormData;
  }>(`/operator/${props.id}/logo`, props.file, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  const data = await response;

  return data;
};

export const useUploadOperatorLogoMutation = (operator_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['operatorLogoUpload'],
    mutationFn: (props: { id: string; file: FormData }) =>
      uploadOperatorLogoRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['operators'] }),
        client.invalidateQueries({
          queryKey: ['operatorLogo', operator_id],
        }),
      ]),
  });

  return mutation;
};

// ---------- DELETE OPERATOR LOGO ----------

const deleteOperatorLogoRequest = async (props: { id: string }) => {
  const response = axiosInstance.delete<{
    id: string;
  }>(`/operator/${props.id}/logo`);
  const data = await response;

  return data;
};

export const useDeleteOperatorLogoMutation = (operator_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['deleteOperatorLogo'],
    mutationFn: (props: { id: string }) => deleteOperatorLogoRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['operators'] }),
        client.invalidateQueries({
          queryKey: ['operatorLogo', operator_id],
        }),
      ]),
  });

  return mutation;
};

// ---------- DOWNLOAD OPERATORS CSV ----------

export const operatorsCsvDownloadRequest = async (
  props: paths['/record/operators/csv']['get']['parameters']['query'],
) => {
  const response = await axiosInstance.get(`/record/operators/csv`, {
    headers: {
      'Content-type': 'application/xml',
    },
    responseType: 'blob',
    params: props,
  });

  const data = (await response).data;

  return data;
};

export const useOperatorsCsvDownloadQuery = () => {
  const mutation = useMutation({
    mutationKey: ['operatorsCSVDownload'],
    mutationFn: (
      props: paths['/record/operators/csv']['get']['parameters']['query'],
    ) => operatorsCsvDownloadRequest(props),
  });

  return mutation;
};
